<template>
  <div class="map-lookup-container">
    <!-- 工具列 -->
    <div class="toolbar-controls">
      <div class="cascade-select-wrapper cascade-select-book">
        <CascadeSelect
          :model-value="bookId"
          :options="bookOptions"
          :is-open="openSelectMenu === 'book'"
          placeholder="書卷"
          @update:model-value="bookId = $event"
          @update:is-open="v => openSelectMenu = v ? 'book' : null"
        />
      </div>

      <div class="cascade-select-wrapper cascade-select-chapter">
        <CascadeSelect
          :model-value="chapter"
          :options="chapterOptions"
          :is-open="openSelectMenu === 'chapter'"
          placeholder="章節"
          :disabled="!bookId"
          @update:model-value="chapter = $event"
          @update:is-open="v => openSelectMenu = v ? 'chapter' : null"
        />
      </div>

      <div class="cascade-select-wrapper cascade-select-verse">
        <CascadeSelect
          :model-value="filterVerse"
          :options="verseOptions"
          :is-open="openSelectMenu === 'verse'"
          placeholder="節次：全部"
          @update:model-value="filterVerse = $event"
          @update:is-open="v => openSelectMenu = v ? 'verse' : null"
        />
      </div>

      <span class="text-sm whitespace-nowrap" style="color: #999;">地名功能暫時停用</span>
    </div>

    <!-- 內容區域：地名列表已禁用（版權考慮） -->
    <div v-if="bookId && chapter" class="content-area">
      <MapContentView :place="null" :map-ref="null" />
    </div>

    <!-- 未選定時的空白提示 -->
    <div v-else class="empty-prompt">
      請先選擇書卷和章節
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useMapLookup } from '../../composables/useMapLookup'
import { apiClient } from '../../services/apiClient'
import CascadeSelect from '../bible-reading/CascadeSelect.vue'
import MapContentView from './MapContentView.vue'

interface Props {
  initialBookId?: number | null
  initialChapter?: number | null
  initialPlaceId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  initialBookId: null,
  initialChapter: null,
  initialPlaceId: null,
})

const { bookId, chapter, filterVerse, openSelectMenu, places, allPlaces, selectedPlaceId, loading, loadChapterPlaces } = useMapLookup()

const books = ref<any[]>([])
const booksLoading = ref(false)
const selectedMapRef = ref<string | null>(null)

onMounted(async () => {
  console.log('MapLookupToolbar onMounted, props:', {
    initialBookId: props.initialBookId,
    initialChapter: props.initialChapter,
    initialPlaceId: props.initialPlaceId,
  })

  try {
    booksLoading.value = true
    const data = await apiClient.getBooks()
    books.value = data.map((b: any) => ({
      id: b.id,
      chiShort: b.chi_short,
      chiFull: b.chi_full,
      engShort: b.eng_short,
      chapterCount: b.chapterCount || 1,
    }))
  } catch (err) {
    console.error('Failed to load books:', err)
  } finally {
    booksLoading.value = false
  }

  // 初始化狀態（由聖經閱讀框觸發開啟時使用）
  const hasInitialBook = props.initialBookId !== null && props.initialBookId !== undefined
  const hasInitialChapter = props.initialChapter !== null && props.initialChapter !== undefined

  if (hasInitialBook) {
    bookId.value = props.initialBookId
  }
  if (hasInitialChapter) {
    chapter.value = props.initialChapter
  }
})

const bookOptions = computed(() =>
  books.value.map(b => ({
    value: b.id,
    label: b.chiFull
  }))
)

const chapters = computed(() => {
  if (!bookId.value) return []
  const book = books.value.find(b => b.id === bookId.value)
  return book ? Array.from({ length: book.chapterCount }, (_, i) => i + 1) : []
})

const chapterOptions = computed(() =>
  chapters.value.map(ch => ({
    value: ch,
    label: `第 ${ch} 章`
  }))
)

const selectedPlace = computed(() =>
  allPlaces.value.find(p => p.id === selectedPlaceId.value) ?? null
)

const verseOptions = computed(() => {
  const options: any[] = [
    { value: null, label: '全部' }
  ]

  if (!allPlaces.value.length) return options

  const verses = new Set<number>()
  allPlaces.value.forEach(place => {
    place.verses.forEach(v => verses.add(v))
  })

  Array.from(verses)
    .sort((a, b) => a - b)
    .slice(0, 20)
    .forEach(v => {
      options.push({
        value: v,
        label: `第 ${v} 節`
      })
    })

  return options
}
)

const fitViewToBounds = () => {
  // 梁天樞與聖光網站自行呈現地圖，無需前端調整
}
</script>

<style scoped>
.map-lookup-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: white;
}

.toolbar-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  overflow: visible;
  flex-wrap: wrap;
  align-content: flex-start;
  min-height: 32px;
}

.toolbar-controls::-webkit-scrollbar {
  display: none;
}

.cascade-select-wrapper {
  position: relative;
  z-index: 100;
  flex-shrink: 0;
  min-width: 70px;
}

.cascade-select-book {
  min-width: 100px;
}

.cascade-select-chapter {
  min-width: 90px;
}

.cascade-select-verse {
  min-width: 90px;
}

.cascade-select-wrapper > :deep(.cascade-select) {
  z-index: 100;
  width: 100%;
}

.cascade-select-wrapper > :deep(.cascade-select-trigger) {
  width: 100%;
}

.cascade-select-wrapper > :deep(.cascade-select-dropdown) {
  z-index: 1000 !important;
}

.icon-btn {
  padding: 4px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.content-area {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

.empty-prompt {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
</style>
